/**
 * Industry-Specific Campaign Templates
 * Based on best practices from vertical SaaS leaders and industry specialists
 *
 * Key insights for industry-specific outreach:
 * - Industry-specific messaging increases response rates by 2-3x
 * - Reference industry-specific pain points and metrics
 * - Use industry terminology and KPIs
 * - Share case studies from the same vertical
 * - Understand buying cycles and decision makers by industry
 */

import { CampaignTemplate } from "./types";

export const INDUSTRY_SAAS: CampaignTemplate = {
  id: "industry-saas",
  name: "SaaS Company Outreach",
  category: "industry",
  description: "Tailored outreach for B2B SaaS companies.",
  strategy:
    "Speaks the language of SaaS - MRR, churn, PLG, customer success. References metrics they care about and positions your solution as a growth lever for their product-led or sales-led motion.",
  icon: "CloudOutlined",
  color: "#06b6d4",
  targetAudience: "b2b-saas",
  personalizationLevel: "high",

  expectedOutcomes: [
    "25-35% open rate (industry relevance)",
    "Higher engagement with SaaS-specific content",
    "Shorter sales cycle with informed buyers",
    "Stronger product fit conversations",
  ],

  bestPractices: [
    "Reference their specific SaaS model (PLG, sales-led, hybrid)",
    "Use metrics they care about (MRR, NRR, churn, activation)",
    "Understand their ICP and how you help serve it",
    "Share case studies from similar-stage SaaS companies",
    "Acknowledge their growth stage and challenges",
  ],

  objectionHandling: [
    "'We're focused on product, not sales' → Our PLG customers use us to improve activation",
    "'We have in-house support' → We help scale without adding headcount",
    "'Our churn is low' → Let's talk about expansion revenue instead",
  ],

  segmentation: {
    leadSource: ["linkedin", "website", "crm", "event"],
    companySize: ["11-50", "51-200", "201-500"],
    industries: ["SaaS", "Technology", "Software"],
    jobTitles: [
      "VP of Customer Success",
      "Head of Growth",
      "VP Marketing",
      "CEO",
      "CTO",
      "Head of Product",
    ],
    requiredFields: ["first_name", "company_name", "email", "job_title"],
  },

  emails: [
    {
      name: "Day 0 - SaaS Pain Point",
      subject: "{{first_name}}, scaling support without scaling headcount",
      suggestedMjmlTemplateId: "coldOutreach-saasPainPoint",
      delayAfterPrevious: { value: 0, unit: "days" },
      purpose: "Lead with a universal SaaS pain point",
      optimalSendTime: {
        dayOfWeek: ["tuesday", "wednesday", "thursday"],
        timeSlot: "morning",
      },
      variables: [
        {
          key: "saas_pain",
          defaultValue: "scaling support as you grow",
          description: "SaaS-specific pain point",
        },
        {
          key: "saas_metric",
          defaultValue: "reduce support tickets by 40%",
          description: "Metric they care about",
        },
        {
          key: "saas_outcome",
          defaultValue: "improving NRR through better customer experience",
          description: "Business outcome",
        },
      ],
    },
    {
      name: "Day 3 - SaaS Case Study",
      subject: "How {{saas_company}} improved activation by {{percentage}}",
      suggestedMjmlTemplateId: "coldOutreach-saasCaseStudy",
      delayAfterPrevious: { value: 3, unit: "days" },
      purpose: "Share case study from similar SaaS company",
      variables: [
        {
          key: "saas_company",
          defaultValue: "a Series B SaaS company",
          description: "Reference SaaS customer",
        },
        {
          key: "percentage",
          defaultValue: "35%",
          description: "Improvement percentage",
        },
        {
          key: "saas_stage",
          defaultValue: "Series B",
          description: "Funding stage of reference",
        },
      ],
    },
    {
      name: "Day 7 - PLG/Sales Motion",
      subject: "{{motion_type}} motion + AI chatbots",
      suggestedMjmlTemplateId: "coldOutreach-saasMotion",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Connect solution to their go-to-market motion",
      variables: [
        {
          key: "motion_type",
          defaultValue: "Product-led growth",
          description: "Their GTM motion (PLG/sales-led)",
        },
        {
          key: "motion_benefit",
          defaultValue: "convert more free users to paid",
          description: "How you help that motion",
        },
      ],
    },
    {
      name: "Day 12 - Growth Stage",
      subject: "For {{growth_stage}} SaaS companies like {{company_name}}",
      suggestedMjmlTemplateId: "coldOutreach-saasGrowthStage",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Speak to their specific growth stage challenges",
      variables: [
        {
          key: "growth_stage",
          defaultValue: "scaling",
          description: "Their growth stage",
        },
        {
          key: "stage_challenge",
          defaultValue: "maintaining support quality while growing fast",
          description: "Challenge at that stage",
        },
      ],
    },
  ],

  sendingConfig: {
    emailsPerDayPerSender: 50,
    dailyLimit: 150,
    requiresWarming: true,
  },

  variables: [
    {
      key: "sender_name",
      defaultValue: "{{user_name}}",
      description: "Sales rep's name",
      source: "crm",
    },
    {
      key: "saas_company",
      defaultValue: "leading SaaS companies",
      description: "Reference customer",
      source: "manual",
    },
    {
      key: "calendar_link",
      defaultValue: "{{booking_link}}",
      description: "Meeting booking link",
      source: "crm",
    },
  ],

  tags: ["industry", "saas", "b2b", "software", "tech"],
  bestFor: ["SaaS Sales Teams", "Tech-focused SDRs", "Vertical Selling"],
  difficulty: "intermediate",
  setupTime: "20 minutes",
  inspiredBy: ["OpenView", "SaaStr", "Tomasz Tunguz"],
};

export const INDUSTRY_ECOMMERCE: CampaignTemplate = {
  id: "industry-ecommerce",
  name: "E-commerce Brand Outreach",
  category: "industry",
  description: "Tailored outreach for e-commerce and DTC brands.",
  strategy:
    "Speaks the language of e-commerce - conversion rates, cart abandonment, customer lifetime value, and peak season preparation. Positions chatbots as a revenue driver, not just a support tool.",
  icon: "ShoppingCartOutlined",
  color: "#10b981",
  targetAudience: "ecommerce",
  personalizationLevel: "high",

  expectedOutcomes: [
    "Higher engagement with revenue-focused messaging",
    "Faster decisions (e-commerce moves fast)",
    "Clear ROI conversations",
    "Seasonal urgency opportunities",
  ],

  bestPractices: [
    "Focus on revenue impact, not just support savings",
    "Reference conversion rate and cart abandonment metrics",
    "Time outreach around peak seasons (BFCM, holidays)",
    "Show how chatbots drive sales, not just answer questions",
    "Use case studies with revenue metrics",
  ],

  objectionHandling: [
    "'We use Zendesk/Gorgias' → We integrate with them and add conversational commerce",
    "'Our conversion rate is already good' → Let's talk about cart recovery and upsells",
    "'Too busy for a new tool' → We can be live before peak season",
  ],

  segmentation: {
    leadSource: ["linkedin", "website", "scraped"],
    companySize: ["11-50", "51-200", "201-500"],
    industries: ["E-commerce", "Retail", "DTC", "Consumer Goods"],
    jobTitles: [
      "VP of E-commerce",
      "Head of Customer Experience",
      "Director of Digital",
      "CMO",
      "CEO",
      "Founder",
    ],
    requiredFields: ["first_name", "company_name", "email"],
  },

  emails: [
    {
      name: "Day 0 - Revenue Focus",
      subject: "{{first_name}}, turning support into sales",
      suggestedMjmlTemplateId: "coldOutreach-ecommerceRevenue",
      delayAfterPrevious: { value: 0, unit: "days" },
      purpose: "Position chatbots as revenue driver",
      optimalSendTime: {
        dayOfWeek: ["tuesday", "wednesday", "thursday"],
        timeSlot: "morning",
      },
      variables: [
        {
          key: "revenue_angle",
          defaultValue: "convert 15% more product questions into purchases",
          description: "Revenue-focused benefit",
        },
        {
          key: "ecom_metric",
          defaultValue: "increase AOV by 23%",
          description: "E-commerce metric improvement",
        },
      ],
    },
    {
      name: "Day 3 - Cart Recovery",
      subject: "Recovering abandoned carts with AI",
      suggestedMjmlTemplateId: "coldOutreach-cartRecovery",
      delayAfterPrevious: { value: 3, unit: "days" },
      purpose: "Focus on cart abandonment use case",
      variables: [
        {
          key: "abandonment_stat",
          defaultValue: "70% of carts are abandoned",
          description: "Cart abandonment statistic",
        },
        {
          key: "recovery_rate",
          defaultValue: "recover 12% more carts",
          description: "Recovery improvement",
        },
      ],
    },
    {
      name: "Day 7 - DTC Case Study",
      subject: "How {{dtc_brand}} increased conversions {{percentage}}",
      suggestedMjmlTemplateId: "coldOutreach-dtcCaseStudy",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Share e-commerce case study",
      variables: [
        {
          key: "dtc_brand",
          defaultValue: "a DTC skincare brand",
          description: "Reference e-commerce customer",
        },
        {
          key: "percentage",
          defaultValue: "27%",
          description: "Conversion improvement",
        },
      ],
    },
    {
      name: "Day 12 - Peak Season",
      subject: "Ready for {{peak_season}}?",
      suggestedMjmlTemplateId: "coldOutreach-peakSeason",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Create urgency around upcoming peak season",
      variables: [
        {
          key: "peak_season",
          defaultValue: "holiday season",
          description: "Upcoming peak season",
        },
        {
          key: "prep_timeline",
          defaultValue: "You could be live in 2 weeks",
          description: "Implementation timeline",
        },
      ],
    },
  ],

  sendingConfig: {
    emailsPerDayPerSender: 50,
    dailyLimit: 150,
    requiresWarming: true,
  },

  variables: [
    {
      key: "sender_name",
      defaultValue: "{{user_name}}",
      description: "Sales rep's name",
      source: "crm",
    },
    {
      key: "dtc_brand",
      defaultValue: "leading DTC brands",
      description: "Reference customer",
      source: "manual",
    },
    {
      key: "calendar_link",
      defaultValue: "{{booking_link}}",
      description: "Meeting booking link",
      source: "crm",
    },
  ],

  tags: ["industry", "ecommerce", "dtc", "retail", "shopify"],
  bestFor: ["E-commerce Sales", "DTC Brands", "Retail Tech"],
  difficulty: "intermediate",
  setupTime: "20 minutes",
  inspiredBy: ["Shopify", "Gorgias", "Klaviyo"],
};

export const INDUSTRY_AGENCY: CampaignTemplate = {
  id: "industry-agency",
  name: "Agency/Consultant Outreach",
  category: "industry",
  description: "Tailored outreach for marketing agencies and consultants.",
  strategy:
    "Agencies think about client outcomes and recurring revenue. Position your solution as something they can offer to clients, creating a new revenue stream or competitive differentiator.",
  icon: "BulbOutlined",
  color: "#8b5cf6",
  targetAudience: "agency",
  personalizationLevel: "moderate",

  expectedOutcomes: [
    "Partnership discussions",
    "Multiple client implementations",
    "Recurring revenue through agency channel",
    "White-label or reseller opportunities",
  ],

  bestPractices: [
    "Focus on client outcomes, not agency operations",
    "Highlight revenue opportunity (markup, service fees)",
    "Mention white-label or co-branded options",
    "Show how you make them look good to clients",
    "Reference other agencies using your solution",
  ],

  objectionHandling: [
    "'We build custom solutions' → We're the foundation you customize on top of",
    "'Clients won't pay extra' → Show ROI that justifies the investment",
    "'We have partnerships already' → We complement, not compete with those",
  ],

  segmentation: {
    leadSource: ["linkedin", "website", "referral", "event"],
    companySize: ["1-10", "11-50", "51-200"],
    industries: ["Marketing Agency", "Digital Agency", "Consulting", "Professional Services"],
    jobTitles: [
      "Agency Owner",
      "Managing Director",
      "Partner",
      "CEO",
      "Founder",
      "Client Services Director",
    ],
    requiredFields: ["first_name", "company_name", "email"],
  },

  emails: [
    {
      name: "Day 0 - Client Value",
      subject: "{{first_name}}, new service for your clients",
      suggestedMjmlTemplateId: "coldOutreach-agencyClientValue",
      delayAfterPrevious: { value: 0, unit: "days" },
      purpose: "Position as new service offering for their clients",
      optimalSendTime: {
        dayOfWeek: ["tuesday", "wednesday", "thursday"],
        timeSlot: "morning",
      },
      variables: [
        {
          key: "service_type",
          defaultValue: "AI-powered customer engagement",
          description: "Service they can offer",
        },
        {
          key: "client_benefit",
          defaultValue: "increase client conversions by 25%",
          description: "Benefit to their clients",
        },
      ],
    },
    {
      name: "Day 4 - Revenue Opportunity",
      subject: "New recurring revenue for {{company_name}}",
      suggestedMjmlTemplateId: "coldOutreach-agencyRevenue",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Highlight revenue opportunity for the agency",
      variables: [
        {
          key: "revenue_model",
          defaultValue: "mark up our service 30-50%",
          description: "Revenue model for agency",
        },
        {
          key: "agency_benefit",
          defaultValue: "recurring revenue from every client implementation",
          description: "Benefit to agency",
        },
      ],
    },
    {
      name: "Day 9 - Agency Case Study",
      subject: "How {{agency_name}} added $10K MRR",
      suggestedMjmlTemplateId: "coldOutreach-agencyCaseStudy",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Share case study from similar agency",
      variables: [
        {
          key: "agency_name",
          defaultValue: "a digital marketing agency",
          description: "Reference agency",
        },
        {
          key: "agency_result",
          defaultValue: "$10K in new monthly recurring revenue",
          description: "Result for the agency",
        },
      ],
    },
    {
      name: "Day 14 - Partnership",
      subject: "Partnership opportunity, {{first_name}}?",
      suggestedMjmlTemplateId: "coldOutreach-agencyPartnership",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Propose formal partnership discussion",
    },
  ],

  sendingConfig: {
    emailsPerDayPerSender: 45,
    dailyLimit: 135,
    requiresWarming: true,
  },

  variables: [
    {
      key: "sender_name",
      defaultValue: "{{user_name}}",
      description: "Partnership/sales rep name",
      source: "crm",
    },
    {
      key: "agency_name",
      defaultValue: "leading agencies",
      description: "Reference agency",
      source: "manual",
    },
    {
      key: "calendar_link",
      defaultValue: "{{booking_link}}",
      description: "Meeting booking link",
      source: "crm",
    },
  ],

  tags: ["industry", "agency", "consultant", "partner", "reseller"],
  bestFor: ["Partner Sales", "Channel Development", "Agency Programs"],
  difficulty: "intermediate",
  setupTime: "20 minutes",
  inspiredBy: ["HubSpot Agency Program", "Klaviyo Partners", "Shopify Partners"],
};

export const INDUSTRY_ENTERPRISE: CampaignTemplate = {
  id: "industry-enterprise",
  name: "Enterprise Account Outreach",
  category: "industry",
  description: "Strategic outreach for Fortune 500 and large enterprises.",
  strategy:
    "Enterprise deals require patience, multi-threading, and a focus on security, compliance, and scalability. Position your solution as enterprise-ready with references from similar-sized companies.",
  icon: "BankOutlined",
  color: "#ef4444",
  targetAudience: "enterprise",
  personalizationLevel: "hyper",

  expectedOutcomes: [
    "10-15% response rate (highly targeted)",
    "Larger deal sizes",
    "Multi-year contracts",
    "Expansion opportunities",
  ],

  bestPractices: [
    "Research the company extensively before outreach",
    "Focus on security, compliance, and scalability",
    "Reference enterprise customers by name (with permission)",
    "Send from senior executive when possible",
    "Be patient - enterprise deals take time",
  ],

  objectionHandling: [
    "'We have procurement processes' → Happy to work with your procurement team",
    "'Need to evaluate multiple vendors' → We're often on the shortlist for [similar companies]",
    "'Security concerns' → We're SOC 2 Type II certified, GDPR compliant...",
  ],

  segmentation: {
    leadSource: ["linkedin", "referral", "event"],
    companySize: ["1000+"],
    industries: ["Technology", "Finance", "Healthcare", "Manufacturing", "Retail"],
    jobTitles: [
      "C-Level",
      "SVP",
      "VP",
      "Executive Director",
      "Managing Director",
    ],
    requiredFields: [
      "first_name",
      "company_name",
      "email",
      "job_title",
      "industry",
    ],
  },

  emails: [
    {
      name: "Day 0 - Enterprise Value",
      subject: "{{first_name}}, customer experience at scale",
      suggestedMjmlTemplateId: "coldOutreach-enterpriseValue",
      delayAfterPrevious: { value: 0, unit: "days" },
      purpose: "Position enterprise-grade solution",
      optimalSendTime: {
        dayOfWeek: ["tuesday", "wednesday"],
        timeSlot: "morning",
      },
      variables: [
        {
          key: "enterprise_challenge",
          defaultValue: "maintaining personalized customer experience at scale",
          description: "Enterprise-specific challenge",
        },
        {
          key: "scale_metric",
          defaultValue: "handle millions of conversations",
          description: "Scale capability",
        },
      ],
    },
    {
      name: "Day 5 - Security & Compliance",
      subject: "Security-first approach for {{company_name}}",
      suggestedMjmlTemplateId: "coldOutreach-enterpriseSecurity",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Address security and compliance concerns upfront",
      variables: [
        {
          key: "security_certs",
          defaultValue: "SOC 2 Type II, GDPR, HIPAA-ready",
          description: "Security certifications",
        },
        {
          key: "enterprise_feature",
          defaultValue: "SSO, audit logs, data residency options",
          description: "Enterprise features",
        },
      ],
    },
    {
      name: "Day 11 - Enterprise Case Study",
      subject: "How {{enterprise_customer}} transformed CX",
      suggestedMjmlTemplateId: "coldOutreach-enterpriseCaseStudy",
      delayAfterPrevious: { value: 6, unit: "days" },
      purpose: "Share Fortune 500 customer success story",
      variables: [
        {
          key: "enterprise_customer",
          defaultValue: "a Fortune 500 company",
          description: "Reference enterprise customer",
        },
        {
          key: "enterprise_result",
          defaultValue: "50% reduction in support costs at scale",
          description: "Result achieved",
        },
      ],
    },
    {
      name: "Day 18 - Executive Briefing",
      subject: "Executive briefing for {{company_name}}",
      suggestedMjmlTemplateId: "coldOutreach-enterpriseBriefing",
      delayAfterPrevious: { value: 7, unit: "days" },
      purpose: "Offer executive briefing or custom proposal",
    },
  ],

  sendingConfig: {
    emailsPerDayPerSender: 20,
    dailyLimit: 60,
    requiresWarming: true,
    minDomainAge: 90,
  },

  variables: [
    {
      key: "sender_name",
      defaultValue: "{{user_name}}",
      description: "Senior sales rep name",
      source: "crm",
    },
    {
      key: "sender_title",
      defaultValue: "{{user_title}}",
      description: "Sales rep title",
      source: "crm",
    },
    {
      key: "enterprise_customer",
      defaultValue: "Fortune 500 customers",
      description: "Reference customer",
      source: "manual",
    },
    {
      key: "calendar_link",
      defaultValue: "{{booking_link}}",
      description: "Meeting booking link",
      source: "crm",
    },
  ],

  tags: ["industry", "enterprise", "fortune-500", "strategic", "security"],
  bestFor: ["Enterprise Sales", "Strategic Accounts", "Large Deals"],
  difficulty: "advanced",
  setupTime: "30 minutes",
  inspiredBy: ["Salesforce", "ServiceNow", "Adobe"],
};

export const INDUSTRY_STARTUP: CampaignTemplate = {
  id: "industry-startup",
  name: "Startup/Scale-up Outreach",
  category: "industry",
  description: "Fast-paced outreach for startups and scale-ups.",
  strategy:
    "Startups move fast and value speed, simplicity, and cost-effectiveness. Focus on quick wins, easy implementation, and how you help them punch above their weight.",
  icon: "RocketOutlined",
  color: "#f59e0b",
  targetAudience: "startup",
  personalizationLevel: "moderate",

  expectedOutcomes: [
    "Faster decision cycles",
    "Higher trial conversion",
    "Land-and-expand opportunities",
    "Product feedback and advocacy",
  ],

  bestPractices: [
    "Emphasize speed and ease of implementation",
    "Offer startup-friendly pricing or trials",
    "Focus on immediate ROI, not long-term vision",
    "Reference other startups at similar stage",
    "Be direct and cut the corporate speak",
  ],

  objectionHandling: [
    "'We're bootstrapped' → We have startup pricing and pay-as-you-grow plans",
    "'We'll build it ourselves' → Focus on your core product, we've got this covered",
    "'Too early for this' → The earlier you start, the more data you'll have",
  ],

  segmentation: {
    leadSource: ["linkedin", "website", "event", "referral"],
    companySize: ["1-10", "11-50"],
    industries: ["Technology", "SaaS", "Fintech", "E-commerce", "Healthtech"],
    jobTitles: [
      "Founder",
      "Co-founder",
      "CEO",
      "CTO",
      "Head of Growth",
      "Head of Product",
    ],
    requiredFields: ["first_name", "company_name", "email"],
  },

  emails: [
    {
      name: "Day 0 - Startup Speed",
      subject: "{{first_name}}, live in 15 minutes",
      suggestedMjmlTemplateId: "coldOutreach-startupSpeed",
      delayAfterPrevious: { value: 0, unit: "days" },
      purpose: "Emphasize speed and ease of getting started",
      optimalSendTime: {
        dayOfWeek: ["tuesday", "wednesday", "thursday"],
        timeSlot: "morning",
      },
      variables: [
        {
          key: "speed_claim",
          defaultValue: "be live with an AI chatbot in 15 minutes",
          description: "Speed benefit",
        },
        {
          key: "startup_benefit",
          defaultValue: "look like a bigger company without the headcount",
          description: "Startup-relevant benefit",
        },
      ],
      abTestSuggestions: {
        subjectVariants: [
          "Quick win for {{company_name}}",
          "Startup hack: AI support without hiring",
          "{{first_name}}, this takes 15 minutes",
        ],
      },
    },
    {
      name: "Day 3 - Startup Pricing",
      subject: "Startup-friendly pricing for {{company_name}}",
      suggestedMjmlTemplateId: "coldOutreach-startupPricing",
      delayAfterPrevious: { value: 3, unit: "days" },
      purpose: "Address budget concerns with startup pricing",
      variables: [
        {
          key: "pricing_offer",
          defaultValue: "50% off for startups under Series A",
          description: "Startup pricing offer",
        },
        {
          key: "free_tier",
          defaultValue: "free tier to get started",
          description: "Free option available",
        },
      ],
    },
    {
      name: "Day 7 - Startup Case Study",
      subject: "How {{startup_name}} scaled to {{metric}}",
      suggestedMjmlTemplateId: "coldOutreach-startupCaseStudy",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Share case study from similar-stage startup",
      variables: [
        {
          key: "startup_name",
          defaultValue: "a YC startup",
          description: "Reference startup",
        },
        {
          key: "metric",
          defaultValue: "10K conversations/month",
          description: "Scale achieved",
        },
        {
          key: "startup_stage",
          defaultValue: "seed stage",
          description: "Funding stage",
        },
      ],
    },
    {
      name: "Day 11 - Quick Call",
      subject: "15 min this week, {{first_name}}?",
      suggestedMjmlTemplateId: "coldOutreach-startupQuickCall",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Quick, low-commitment meeting request",
    },
  ],

  sendingConfig: {
    emailsPerDayPerSender: 60,
    dailyLimit: 180,
    requiresWarming: true,
  },

  variables: [
    {
      key: "sender_name",
      defaultValue: "{{user_name}}",
      description: "Sales rep name",
      source: "crm",
    },
    {
      key: "startup_name",
      defaultValue: "fast-growing startups",
      description: "Reference startup",
      source: "manual",
    },
    {
      key: "calendar_link",
      defaultValue: "{{booking_link}}",
      description: "Meeting booking link",
      source: "crm",
    },
  ],

  tags: ["industry", "startup", "scale-up", "yc", "fast-growth"],
  bestFor: ["Startup Sales", "PLG Motion", "Volume Sales"],
  difficulty: "beginner",
  setupTime: "15 minutes",
  inspiredBy: ["Y Combinator", "a]a16z", "Sequoia"],
};

export const INDUSTRY_TEMPLATES = [
  INDUSTRY_SAAS,
  INDUSTRY_ECOMMERCE,
  INDUSTRY_AGENCY,
  INDUSTRY_ENTERPRISE,
  INDUSTRY_STARTUP,
];
